import {
  useThemeStore,
  themeLabels,
  themeDescriptions,
  ThemeMode,
} from "../store/themeStore";
import { useCalculatorStore } from "../store/calculatorStore";

export function SettingsPanel() {
  const { theme, setTheme } = useThemeStore();
  const { resetEnvironment, clearHistory } = useCalculatorStore();

  const themes: ThemeMode[] = [
    "dark",
    "light",
    "high-contrast-dark",
    "high-contrast-light",
  ];

  return (
    <div className="settings-panel">
      <div className="settings-header">
        <h2>Settings</h2>
      </div>

      <div className="settings-content">
        {/* Appearance Section */}
        <section className="settings-section-card">
          <h3 className="settings-section-heading">
            <AppearanceIcon />
            Appearance
          </h3>
          <p className="settings-section-desc">
            Choose a theme that works best for you. High contrast themes are
            optimized for visibility.
          </p>

          <div
            className="theme-grid"
            role="radiogroup"
            aria-label="Theme selection"
          >
            {themes.map((themeOption) => (
              <button
                key={themeOption}
                className={`theme-card ${theme === themeOption ? "active" : ""}`}
                onClick={() => setTheme(themeOption)}
                role="radio"
                aria-checked={theme === themeOption}
              >
                <div className="theme-card-preview">
                  <ThemePreview theme={themeOption} />
                </div>
                <div className="theme-card-info">
                  <span className="theme-card-label">
                    {themeLabels[themeOption]}
                  </span>
                  <span className="theme-card-description">
                    {themeDescriptions[themeOption]}
                  </span>
                </div>
                {theme === themeOption && (
                  <div className="theme-card-check">
                    <CheckIcon />
                  </div>
                )}
              </button>
            ))}
          </div>
        </section>

        {/* Calculator Section */}
        <section className="settings-section-card">
          <h3 className="settings-section-heading">
            <CalculatorIcon />
            Calculator
          </h3>
          <p className="settings-section-desc">
            Manage your calculator environment and data.
          </p>

          <div className="settings-actions">
            <div className="settings-action-item">
              <div className="settings-action-info">
                <span className="settings-action-label">Reset Environment</span>
                <span className="settings-action-desc">
                  Clear all variables and start fresh
                </span>
              </div>
              <button className="btn-secondary" onClick={resetEnvironment}>
                Reset
              </button>
            </div>

            <div className="settings-action-item">
              <div className="settings-action-info">
                <span className="settings-action-label">Clear History</span>
                <span className="settings-action-desc">
                  Delete all calculation history
                </span>
              </div>
              <button className="btn-danger" onClick={clearHistory}>
                Clear
              </button>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="settings-section-card">
          <h3 className="settings-section-heading">
            <InfoIcon />
            About
          </h3>
          <div className="settings-about">
            <div className="settings-about-logo">
              <img src={`${import.meta.env.BASE_URL}assets/quanta.png`} alt="Quanta" />
            </div>
            <div className="settings-about-info">
              <span className="settings-about-name">Quanta</span>
              <span className="settings-about-version">Version 0.1.0</span>
              <span className="settings-about-desc">
                Advanced scientific calculator with matrix operations and
                plotting
              </span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function ThemePreview({ theme }: { theme: ThemeMode }) {
  const colors = {
    dark: {
      bg: "#0a0a0f",
      secondary: "#1a1a24",
      accent: "#6366f1",
      text: "#f0f0f5",
    },
    light: {
      bg: "#f8f9fc",
      secondary: "#ffffff",
      accent: "#4f46e5",
      text: "#1a1a2e",
    },
    "high-contrast-dark": {
      bg: "#000000",
      secondary: "#141414",
      accent: "#818cf8",
      text: "#ffffff",
    },
    "high-contrast-light": {
      bg: "#ffffff",
      secondary: "#f5f5f5",
      accent: "#3730a3",
      text: "#000000",
    },
  };
  const c = colors[theme];

  return (
    <svg viewBox="0 0 80 50" className="theme-preview-svg">
      {/* Background */}
      <rect width="80" height="50" fill={c.bg} rx="4" />
      {/* Header */}
      <rect width="80" height="10" fill={c.secondary} />
      {/* Content area */}
      <rect
        x="5"
        y="15"
        width="30"
        height="6"
        fill={c.text}
        opacity="0.3"
        rx="1"
      />
      <rect x="5" y="24" width="45" height="6" fill={c.accent} rx="1" />
      <rect
        x="5"
        y="33"
        width="25"
        height="6"
        fill={c.text}
        opacity="0.2"
        rx="1"
      />
      {/* Sidebar hint */}
      <rect x="60" y="15" width="15" height="30" fill={c.secondary} rx="2" />
    </svg>
  );
}

function AppearanceIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2v20M2 12h20" opacity="0.3" />
      <circle cx="12" cy="12" r="4" />
    </svg>
  );
}

function CalculatorIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="8" y1="10" x2="8" y2="10.01" />
      <line x1="12" y1="10" x2="12" y2="10.01" />
      <line x1="16" y1="10" x2="16" y2="10.01" />
      <line x1="8" y1="14" x2="8" y2="14.01" />
      <line x1="12" y1="14" x2="12" y2="14.01" />
      <line x1="16" y1="14" x2="16" y2="14.01" />
      <line x1="8" y1="18" x2="8" y2="18.01" />
      <line x1="12" y1="18" x2="12" y2="18.01" />
      <line x1="16" y1="18" x2="16" y2="18.01" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
